import {
  Table,
  TableColumnsType,
  Modal,
  Tabs,
  Form,
  FormProps,
  Select,
  Input,
  Radio,
  Cascader,
} from 'antd';
import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import { Box } from '@mui/material';
import { Button, Message } from '@alifd/next';
import { ParkingSpace } from '@/types/database/parkingSpaces';
import { ResidentAccountNumber } from '@/types/database/residentAccountNumbers';
import { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import { RootState } from '@/redux/store';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import DownloadButton from '@/components/UI/DownloadButton';
import PageHeader from '@/components/PageHeader';
import useService from '@/lib/hooks/useService';

import UploadParkingSpaces from './components/UploadParkingSpaces';
import type { EditFormValues } from './types';
import { getSorter } from './utils';

const ParkingSpaceList: React.FC = () => {
  const [vehiclePage, setVehiclePage] = useState<'car' | 'motorcycle'>('car');
  const [selectedParkingSpace, setSelectedParkingSpace] =
    useState<ParkingSpace | null>(null);
  const [openEditModal, setOpenEditModal] = useState(false);

  const { Item, useForm } = Form;

  const [editForm] = useForm<EditFormValues>();

  const user = useSelector((state: RootState) => state.user);

  const [parkingSpaces, updateParkingSpaces, parkingSpacesClient] =
    useService<ParkingSpace>({
      collectionName: 'parkingSpaces',
    });
  const [residentAccountNumbers] = useService<ResidentAccountNumber>({
    collectionName: 'residentAccountNumbers',
  });
  const [chargingPiles] = useService<ChargingPile>({
    collectionName: 'chargingPile',
  });

  const handleSubmitForm: FormProps<EditFormValues>['onFinish'] = useCallback(
    async (values: EditFormValues) => {
      if (selectedParkingSpace) {
        const newData: ParkingSpace = {
          ...values,
          house: null,
          chargingPile: null,
        };
        if (values.house) {
          newData.house =
            residentAccountNumbers.find(
              (house) => house.householdNumber === values?.house?.join('-')
            ) || null;
        }
        if (values.chargingPile) {
          newData.chargingPile =
            chargingPiles.find((pile) => pile._id === values.chargingPile) ||
            null;
        }
        try {
          await parkingSpacesClient.patch(selectedParkingSpace._id, newData);
          updateParkingSpaces();
          setOpenEditModal(false);
          setSelectedParkingSpace(null);
          Message.success('修改成功');
        } catch (err) {
          console.error(err);
          Message.error('更新失敗');
        }
      }
    },
    [
      chargingPiles,
      parkingSpacesClient,
      residentAccountNumbers,
      selectedParkingSpace,
      updateParkingSpaces,
    ]
  );

  const handleDelete = useCallback(
    async (id: ParkingSpace['_id']) => {
      if (!id) {
        return;
      }
      const { isConfirmed } = await Swal.fire({
        title: '確定要刪除嗎？',
        text: '刪除後將無法復原',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        confirmButtonColor: '#209BFA',
        cancelButtonText: '取消',
        cancelButtonColor: '#d33',
      });
      if (isConfirmed) {
        try {
          await parkingSpacesClient.remove(id);
          updateParkingSpaces();
          Message.success('刪除成功');
        } catch (err) {
          console.error(err);
          Message.error('刪除失敗');
        }
      }
    },
    [parkingSpacesClient, updateParkingSpaces]
  );

  const handleCancel = useCallback(() => {
    setOpenEditModal(false);
    setSelectedParkingSpace(null);
    editForm.resetFields();
  }, [editForm]);

  const columns = useMemo(() => {
    const newColumns: TableColumnsType<ParkingSpace> = [
      {
        title: '車位編號',
        key: 'parkingSpaceNumber',
        dataIndex: 'parkingNumber',
        sorter: getSorter('parkingNumber'),
        sortDirections: ['descend', 'ascend', 'descend'],
      },
      {
        title: '戶號',
        key: 'residentAccountNumber',
        dataIndex: 'house',
        render: (house: ParkingSpace['house']) =>
          house?.householdNumber || '未綁定',
        sorter: getSorter('house'),
        sortDirections: ['descend', 'ascend', 'descend'],
      },
      {
        title: '操作',
        key: 'action',
        render: (_, record: ParkingSpace) => (
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
            }}
          >
            <Button
              type="primary"
              onClick={() => {
                setSelectedParkingSpace(record);
                editForm.setFieldsValue({
                  parkingNumber: record.parkingNumber,
                  vehicleType: record.vehicleType,
                  house: record.house?.householdNumber.split('-'),
                  chargingPile: record.chargingPile?._id,
                });
                setOpenEditModal(true);
              }}
            >
              編輯
            </Button>
            <Button
              type="primary"
              warning
              onClick={() => handleDelete(record._id)}
            >
              刪除
            </Button>
          </Box>
        ),
      },
    ];
    if (vehiclePage === 'car') {
      newColumns.splice(-1, 0, {
        title: '車牌',
        key: 'licensePlateNumber',
        dataIndex: 'house',
        render: (house: ParkingSpace['house']) => {
          if (house) {
            return house.carNumber.join(', ');
          }
          return '無登記車輛';
        },
      });
    }
    if (
      user?.adminUserGroup?.allow?.some(
        (item: any) => item.name === '汽車充電樁'
      ) &&
      vehiclePage === 'car'
    ) {
      newColumns.splice(-1, 0, {
        title: '充電樁編號',
        key: 'chargingPileNumber',
        dataIndex: 'chargingPile',
        render: (chargingPile: ParkingSpace['chargingPile']) =>
          chargingPile?.chargingPileID || '未綁定',
        sorter: getSorter('chargingPile'),
        sortDirections: ['descend', 'ascend', 'descend'],
      });
    }

    return newColumns;
  }, [user?.adminUserGroup?.allow, vehiclePage, editForm, handleDelete]);

  const dataSource = useMemo(
    () =>
      parkingSpaces
        .filter((item) => item.vehicleType === vehiclePage)
        .map((item) => ({ ...item, key: item._id })),
    [parkingSpaces, vehiclePage]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <PageHeader
        title="車位列表"
        breadcrumbs={[
          {
            name: '社區管理',
          },
          {
            name: '車位列表',
          },
        ]}
      />
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
        }}
      >
        <UploadParkingSpaces onUpdate={updateParkingSpaces} />
        <DownloadButton
          url="/ezcon/api/parkingSpacesExcel"
          responseFileType="blob"
          params={{ vehicleType: vehiclePage, isExample: false }}
        >
          匯出車位資料
        </DownloadButton>
        <DownloadButton
          url="/ezcon/api/parkingSpacesExcel"
          responseFileType="blob"
          params={{ vehicleType: vehiclePage, isExample: true }}
        >
          匯出範例資料
        </DownloadButton>
      </Box>
      <Tabs
        onChange={(key: 'car' | 'motorcycle') => setVehiclePage(key)}
        size="large"
        items={[
          {
            key: 'car',
            label: '汽車',
          },
          {
            key: 'motorcycle',
            label: '機車',
          },
        ]}
      />
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          padding: '10px',
          borderRadius: '5px',
        }}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          showSorterTooltip={false}
          bordered
        />
      </Box>
      <Modal
        title="編輯車位"
        open={openEditModal}
        onCancel={handleCancel}
        footer={
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
          >
            <Button type="primary" onClick={() => editForm.submit()}>
              確定
            </Button>
            <Button type="primary" warning onClick={handleCancel}>
              取消
            </Button>
          </Box>
        }
        forceRender
      >
        <Form
          labelCol={{
            span: 6,
          }}
          wrapperCol={{
            span: 18,
          }}
          form={editForm}
          onFinish={handleSubmitForm}
        >
          <Item
            label="車位編號"
            name="parkingNumber"
            rules={[
              {
                required: true,
                message: '請輸入車位編號',
              },
            ]}
          >
            <Input placeholder="車位編號" />
          </Item>
          <Item
            label="車輛類型"
            name="vehicleType"
            rules={[
              {
                required: true,
                message: '請選擇車輛類型',
              },
            ]}
          >
            <Radio.Group>
              <Radio value="car">汽車</Radio>
              <Radio value="motorcycle">機車</Radio>
            </Radio.Group>
          </Item>
          <Item label="戶號" name="house">
            <Cascader
              placeholder="請選擇戶號"
              options={generateHouseHoldNumberCascader(
                residentAccountNumbers.map((house) => house.householdNumber)
              )}
              allowClear
            />
          </Item>
          {vehiclePage === 'car' && (
            <Item label="充電樁編號" name="chargingPile">
              <Select
                placeholder="請選擇充電樁編號"
                options={chargingPiles
                  .map((chargingPile) => ({
                    label: chargingPile.chargingPileID,
                    value: chargingPile._id,
                  }))
                  .sort((a, b) =>
                    a.label
                      .padStart(4, '0')
                      .localeCompare(b.label.padStart(4, '0'))
                  )}
                allowClear
              />
            </Item>
          )}
        </Form>
      </Modal>
    </Box>
  );
};

export default ParkingSpaceList;
